import React, { useState } from 'react';
import Button from './ui/Button';
import Textarea from './ui/Textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import Badge from './ui/Badge';
import { Loader2, Sparkles, ThumbsUp, ThumbsDown, Minus } from 'lucide-react';
import api from '../lib/api';

interface AIMessageImproverProps {
  initialMessage?: string;
  onImprove: (improvedMessage: string) => void;
  targetTone?: 'professional' | 'friendly' | 'diplomatic' | 'urgent';
}

interface SentimentAnalysis {
  sentiment: 'positive' | 'neutral' | 'negative';
  confidence: number;
  suggestions?: string[];
}

interface ImprovedMessage {
  body: string;
  suggestions?: string[];
}

export const AIMessageImprover: React.FC<AIMessageImproverProps> = ({
  initialMessage = '',
  onImprove,
  targetTone = 'professional',
}) => {
  const [message, setMessage] = useState(initialMessage);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isImproving, setIsImproving] = useState(false);
  const [sentiment, setSentiment] = useState<SentimentAnalysis | null>(null);
  const [improvedMessage, setImprovedMessage] = useState<ImprovedMessage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedTone, setSelectedTone] = useState(targetTone);

  const analyzeSentiment = async () => {
    if (!message.trim()) {
      setError('Please enter a message to analyze');
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setSentiment(null);

    try {
      const response = await api.post('/ai-messages/analyze-sentiment', {
        message: message.trim(),
      });
      setSentiment(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to analyze sentiment');
      console.error('Sentiment analysis error:', err);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const improveMessageTone = async () => {
    if (!message.trim()) {
      setError('Please enter a message to improve');
      return;
    }

    setIsImproving(true);
    setError(null);
    setImprovedMessage(null);

    try {
      const response = await api.post('/ai-messages/improve-message', {
        message: message.trim(),
        tone: selectedTone,
      });
      setImprovedMessage(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to improve message');
      console.error('Message improvement error:', err);
    } finally {
      setIsImproving(false);
    }
  };

  const handleAcceptImproved = () => {
    if (improvedMessage) {
      onImprove(improvedMessage.body);
      setMessage(improvedMessage.body);
      setImprovedMessage(null);
      setSentiment(null);
    }
  };

  const getSentimentIcon = (sentimentType: 'positive' | 'neutral' | 'negative') => {
    switch (sentimentType) {
      case 'positive':
        return <ThumbsUp className="h-4 w-4 text-green-600" />;
      case 'negative':
        return <ThumbsDown className="h-4 w-4 text-red-600" />;
      default:
        return <Minus className="h-4 w-4 text-gray-600" />;
    }
  };

  const getSentimentColor = (sentimentType: 'positive' | 'neutral' | 'negative') => {
    switch (sentimentType) {
      case 'positive':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'negative':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <Card className="border-blue-200 bg-blue-50/50">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-blue-600" />
          <CardTitle className="text-lg">AI Message Improver</CardTitle>
        </div>
        <CardDescription>
          Analyze sentiment and improve tone of your message
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Message Input */}
        <div>
          <label className="text-sm font-medium text-gray-700">Your Message:</label>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Enter or paste your message here..."
            className="mt-1 min-h-[120px]"
          />
        </div>

        {/* Error Display */}
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-2">
          <Button
            onClick={analyzeSentiment}
            disabled={isAnalyzing || !message.trim()}
            variant="outline"
            className="w-full"
          >
            {isAnalyzing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <ThumbsUp className="mr-2 h-4 w-4" />
                Analyze Tone
              </>
            )}
          </Button>

          <Button
            onClick={improveMessageTone}
            disabled={isImproving || !message.trim()}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            {isImproving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Improving...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Improve Message
              </>
            )}
          </Button>
        </div>

        {/* Tone Selection */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            Target Tone:
          </label>
          <div className="flex flex-wrap gap-2">
            {(['professional', 'friendly', 'diplomatic', 'urgent'] as const).map((tone) => (
              <Badge
                key={tone}
                variant={selectedTone === tone ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setSelectedTone(tone)}
              >
                {tone.charAt(0).toUpperCase() + tone.slice(1)}
              </Badge>
            ))}
          </div>
        </div>

        {/* Sentiment Analysis Results */}
        {sentiment && (
          <div className="p-4 rounded-lg border bg-white">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-sm">Sentiment Analysis</h4>
              <div className="flex items-center gap-2">
                {getSentimentIcon(sentiment.sentiment)}
                <Badge className={getSentimentColor(sentiment.sentiment)}>
                  {sentiment.sentiment.charAt(0).toUpperCase() + sentiment.sentiment.slice(1)}
                </Badge>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Confidence:</span>
                <span className="font-medium">
                  {(sentiment.confidence * 100).toFixed(0)}%
                </span>
              </div>
              {sentiment.suggestions && sentiment.suggestions.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Suggestions:</p>
                  <ul className="space-y-1 text-sm text-gray-600">
                    {sentiment.suggestions.map((suggestion, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-blue-600 mt-0.5">•</span>
                        <span>{suggestion}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Improved Message */}
        {improvedMessage && (
          <div className="space-y-3 p-4 rounded-lg border bg-white">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-sm">Improved Message</h4>
              <Badge className="bg-blue-100 text-blue-800">
                {selectedTone.charAt(0).toUpperCase() + selectedTone.slice(1)} Tone
              </Badge>
            </div>
            <div className="p-3 bg-gray-50 rounded border text-sm whitespace-pre-wrap">
              {improvedMessage.body}
            </div>
            {improvedMessage.suggestions && improvedMessage.suggestions.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Changes Made:</p>
                <ul className="space-y-1 text-sm text-gray-600">
                  {improvedMessage.suggestions.map((suggestion, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-blue-600 mt-0.5">•</span>
                      <span>{suggestion}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex gap-2">
              <Button
                onClick={handleAcceptImproved}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                Use Improved Version
              </Button>
              <Button
                onClick={() => setImprovedMessage(null)}
                variant="outline"
                className="flex-1"
              >
                Discard
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
